import { z } from "zod";

import { pendingEmailSourceSchema } from "@signalist/shared-types";

import { MetaDataSchema } from "@/schemas/meta-data";
import { ERROR_TYPE } from "@/utils";

import { ConversationMessageType } from "@/enum/conversation.enum";

import { ProspectInterest } from "@/enum/prospect.enum";

import {
	DeepResearchPointSchema,
	DeepResearchSourceSchema,
} from "@/pages/Private/redux/deepResearch/schema";

import {
	LeadScoringBreakdownSchema,
	PersonaCategoryResultsSchema,
	type PersonaCategoryResults,
} from "./lead-scoring-breakdown.schema";
import { EmailSchema } from "../../Account/schema/email";
import {
	LinkedinSignalPostSchema,
	RawSignalSchema,
	ScoredSignalSchema,
} from "../../ProfileTracker/schema/trackerSignalResult";
import {
	CrmDealSchema,
	CrmLeadSchema,
	CrmOrganizationSchema,
	CrmProspectSchema,
} from "./get-people-crm.schema";

const { REQUIRED } = ERROR_TYPE;

// CRM sync schemas moved to a sibling file (size ratchet); re-exported for existing imports.
export * from "./get-people-crm.schema";

export const UserSchema = z.object({
	id: z.number().gt(0),
	firstName: z.string().min(1),
	lastName: z.string().min(1),
	user: z.object({
		id: z.number().gt(0),
		firstName: z.string().min(1),
		lastName: z.string().min(1),
	}),
});

export const ScoreCompanyCategorySchema = z.object({
	id: z.number().optional(),
	description: z.string(),
	category: z.string(),
	type: z.string(),
});

export const CompanyCategoryResultsSchema = z.object({
	id: z.number(),
	result: z.string(),
	reason: z.string(),
	scoreCompanyCategory: ScoreCompanyCategorySchema.nullable(),
	suggestedNewSubCategory: z
		.object({
			name: z.string(),
			description: z.string(),
		})
		.optional()
		.nullable(),
	suggestedChangedSubCategory: z
		.object({
			name: z.string(),
			description: z.string(),
		})
		.optional()
		.nullable(),
	suggestedChangedCategory: z
		.object({
			description: z.string(),
		})
		.optional()
		.nullable(),
	companyCategory: z.string().nullable().optional(),
	leadScore: z.string().nullable().optional(),
	/**
	 * The company-only score's explanation and per-criterion breakdown.
	 *
	 * This used to be declared as `leadScoreReasoning`, a spelling nothing in the
	 * API ever emitted — the column is `leadScoringReasoning`. The field therefore
	 * arrived `undefined` on every response and each C1/C2/C3 hover silently fell
	 * back to the generic "company information only" note, never showing why the
	 * company scored what it did.
	 */
	leadScoringReasoning: z.string().nullable().optional(),
	leadScoringBreakdown: LeadScoringBreakdownSchema.nullable().optional(),
});

export const UserProspectRelationSchema = z.object({
	companyName: z.string().optional().nullable(),
	connectedAt: z
		.string()
		.transform((arg) => new Date(arg))
		.optional()
		.nullable(),
	createdAt: z
		.string()
		.transform((arg) => new Date(arg))
		.optional()
		.nullable(),
	email: z.string().optional().nullable(),
	firstName: z.string().optional().nullable(),
	lastName: z.string().optional().nullable(),
	linkedinUrl: z.string().optional().nullable(),
	prospectId: z.number().optional().nullable(),
	updatedAt: z
		.string()
		.transform((arg) => new Date(arg))
		.optional()
		.nullable(),
	userId: z.number().optional().nullable(),
	title: z.string().optional().nullable(),
});

export const ProspectEmploymentsSchema = z.object({
	id: z.number(),
	title: z.string(),
	organizationName: z.string(),
	current: z.boolean(),
	startedAt: z.string(),
	endedAt: z.string().nullable(),
	linkedinUrl: z.string().nullable(),
});

export const JobTitleToSenioritySchema = z.object({
	id: z.number(),
	seniority: z.string(),
	jobTitle: z.string(),
});

export const ConversationEventSchema = z.object({
	id: z.number(),
	conversationId: z.number(),
	event: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
	reaction: z.string().nullable(),
	reactionBy: z.string().nullable(),
	reactionByLinkedinUrl: z.string().nullable(),
	reactionByLinkedinUrn: z.string().nullable(),
});

// Wrong-person referral extracted by the API's Sentiment Agent (PREC-3584): who the
// lead points to instead — a named person and/or a department (people-search taxonomy id).
export const ConversationReferralDataSchema = z.object({
	type: z.enum(["person", "department"]),
	name: z.string().nullable(),
	department: z.string().nullable(),
});

export type ConversationReferralData = z.infer<typeof ConversationReferralDataSchema>;

export const ConversationSchema = z.object({
	id: z.number(),
	threadId: z.string(),
	type: z.string(),
	emailId: z.number(),
	email: EmailSchema,
	seenAt: z.string(),
	sentAt: z.string(),
	openedAt: z.string(),
	message: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
	user: UserSchema.nullable(),
	// Some endpoints (GET list/unassigned) serve the sending user's id without the user object;
	// read the sender through `conversationSenderUserId` (PREC-4312).
	userId: z.number().nullable().optional(),
	stepId: z.string().nullable(),
	receiverEmail: z.string().nullable(),
	senderEmail: z.string().nullable(),
	senderLinkedinUrl: z.string().nullable(),
	receiverLinkedinUrl: z.string().nullable(),
	campaignId: z.string().nullable(),
	scheduled: z.boolean().nullable(),
	scheduledFor: z.string().nullable(),
	messageType: z.nativeEnum(ConversationMessageType).nullable(),
	// PREC-4091: chat message or InMail; null on rows written before the column existed.
	linkedinMessageKind: z.enum(["message", "inmail"]).nullable().optional(),
	sentiment: z.nativeEnum(ProspectInterest).nullable(),
	referralData: ConversationReferralDataSchema.nullable().optional(),
	listPersonId: z.number().nullable(),
	listId: z.number().nullable().optional(),
	prospectId: z.number().nullable(),
	wasLastConversation: z.boolean().nullable().optional(),
	// PREC-3911: the message belongs to a colleague's own conversation with this person,
	// not to the lead's outreach. The thread collapses these instead of inlining them.
	isOtherAccount: z.boolean().nullable().optional(),
	// PREC-4305: set on a message "Load earlier conversation" imported from the viewer's own
	// LinkedIn or mailbox. Never on a synced row; the API serves these from their own endpoint.
	importedAt: z.string().nullable().optional(),
	// Who imported it, and whether only they can see it (imported on a private lead).
	importedByName: z.string().nullable().optional(),
	importedPrivate: z.boolean().nullable().optional(),
	senderName: z.string().nullable(),
	// Raw JSON with two historical shapes: Unipile attendee objects on received
	// mail, plain email strings on manually scheduled replies. Kept as an opaque
	// array; `CcRecipientsLine.toCcAttendees` narrows the entries.
	ccReceiverEmail: z.array(z.unknown()).nullable(),
	conversationEvents: z.array(ConversationEventSchema).nullable().optional(),
	hasCalendarInvite: z.boolean().nullable().optional(),
	// PREC-3901: set when the mail is the lead's calendar client answering our invitation
	// (ACCEPTED / TENTATIVE / DECLINED / RESCHEDULED). Rendered as a status line, not a message.
	calendarResponse: z.string().nullable().optional(),
	subject: z.string().nullable().optional(),
	calendarInviteData: z
		.object({
			uid: z.string(),
			eventSummary: z
				.union([z.string(), z.object({ val: z.string(), params: z.any() })])
				.transform((v) => (typeof v === "object" && v !== null && "val" in v ? v.val : v)),
			eventStart: z.string(),
			eventEnd: z.string(),
			eventLocation: z
				.union([z.string(), z.object({ val: z.string(), params: z.any() })])
				.transform((v) => (typeof v === "object" && v !== null && "val" in v ? v.val : v))
				.nullable(),
			eventDescription: z
				.union([z.string(), z.object({ val: z.string(), params: z.any() })])
				.transform((v) => (typeof v === "object" && v !== null && "val" in v ? v.val : v))
				.nullable(),
			organizer: z
				.union([z.string(), z.object({ val: z.string(), params: z.any() })])
				.transform((v) => (typeof v === "object" && v !== null && "val" in v ? v.val : v))
				.nullable(),
			attendeeEmail: z.string(),
			attachmentId: z.string(),
			sequence: z.number(),
			status: z.string(),
			rawIcs: z.string(),
		})
		.nullable()
		.optional(),
});

export const ConversationsSchema = z.array(ConversationSchema);

export const TrackerPersonSchema = z.object({
	id: z.number(),
	name: z.string(),
	prospectId: z.number(),
	trackerOrganizationId: z.number(),
	trackerId: z.number(),
});

export const ProspectPendingEmailSchema = z.object({
	id: z.number(),
	email: z.string(),
	source: pendingEmailSourceSchema.optional().nullable(),
});

export type ProspectPendingEmail = z.infer<typeof ProspectPendingEmailSchema>;

export const PeopleSchemaReplica = z.object({
	id: z.number(),
	email: z.string().nullable(),
	name: z.string().min(4, { message: REQUIRED }).trim().nullable(),
	linkedinUrl: z.string().nullable(),
	firstName: z.string().optional().nullable(),
	lastName: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	blacklist: z.boolean().optional().nullable(),
	headline: z.string().optional().nullable(),
	emailStatus: z.string().optional().nullable(),
	pendingEmails: z.array(ProspectPendingEmailSchema).optional().nullable(),
	seniority: z.string().optional().nullable(),
	city: z.string().optional().nullable(),
	state: z.string().optional().nullable(),
	country: z.string().optional().nullable(),
	gender: z.string().optional().nullable(),
	genderProbability: z.number().optional().nullable(),
	users: z.array(UserProspectRelationSchema).optional().nullable(),
	language: z.string().optional().nullable(),
	image: z.string().optional().nullable(),
	phone: z.string().optional().nullable(),
	phoneStatus: z.string().optional().nullable(),
	followers: z.number().optional().nullable(),
	prospectEmployments: z.array(ProspectEmploymentsSchema).optional().nullable(),
	about: z.string().nullable(),
	jobTitleToSeniority: JobTitleToSenioritySchema.nullable(),
	scorePersonaCategoryResult: PersonaCategoryResultsSchema.nullable().optional(),
	jobChangeOrPromotion: z.string().nullable(),
	startedCurrentPosition: z.string().nullable(),
	crmProspects: z.array(CrmProspectSchema).nullable().optional(),
	crmLeads: z.array(CrmLeadSchema).nullable().optional(),
	conversations: z.array(ConversationSchema).optional().nullable(),
	snoozed: z.boolean().optional(),
	snoozedUntil: z
		.string()
		.transform((arg) => new Date(arg))
		.optional()
		.nullable(),
	sentiment: z.nativeEnum(ProspectInterest).nullable(),
	trackerPersons: z.array(TrackerPersonSchema).optional().nullable(),
});

export const TrackerSignalResultAdditionalSchema = z.object({
	id: z.number(),
	trackerOrganizationId: z.number().nullable(),
	trackerPersonId: z.number().nullable(),
	trackerId: z.number().nullable().optional(),
	listId: z.number().nullable().optional(),
	companyId: z.number().nullable().optional(),
	status: z.string(), // Using string to handle various status values
	rawSignal: RawSignalSchema,
	scoredSignal: ScoredSignalSchema,
	signalCategory: z.string().nullable().optional(),
	priority: z.string().nullable().optional(),
	title: z.string().nullable().optional(),
	summary: z.string().nullable().optional(),
	sourceUrl: z.string().nullable().optional(),
	publishedDate: z.string().nullable().optional(),
	reasoning: z.string().nullable().optional(),
	signalCollectedAt: z.string().nullable().optional(),
	signalScoredAt: z.string().nullable().optional(),
	errorMessage: z.string().nullable().optional(),
	linkedinPostUrn: z.string().nullable().optional(),
	linkedinPost: LinkedinSignalPostSchema.nullable().optional(),
	linkedinCommentary: z.string().nullable().optional(),
	createdAt: z.string().transform((arg) => new Date(arg)),
	updatedAt: z
		.string()
		.transform((arg) => new Date(arg))
		.optional(),
});

export const OrganizationSchema = z.object({
	id: z.number(),
	name: z.string(),
	/** Email-form company name; null until resolved, so fall back to `name` (PREC-2748). */
	nameNormalized: z.string().optional().nullable(),
	startup: z.boolean(),
	foundedYear: z.number().nullable(),
	logoUrl: z.string().optional().nullable(),
	estimatedNumEmployees: z.number().optional().nullable(),
	annualRevenue: z.number().optional().nullable(),
	linkedinUrl: z.string().optional().nullable(),
	websiteUrl: z.string().optional().nullable(),
	industry: z.string().optional().nullable(),
	city: z.string().optional().nullable(),
	state: z.string().optional().nullable(),
	country: z.string().optional().nullable(),
	phone: z.string().optional().nullable(),
	primaryDomain: z.string().optional().nullable(),
	locations: z.number().optional().nullable(),
	shortDescription: z.string().optional().nullable(),
	seoDescription: z.string().optional().nullable(),
	followerCount: z.number().optional().nullable(),
	companyLocations: z
		.array(
			z.object({
				city: z.string().nullable(),
				state: z.string().nullable(),
				country: z.string().nullable(),
			})
		)
		.optional()
		.nullable(),
	keywords: z.array(z.string()).optional().nullable(),
	companyLinkedinId: z.string().optional().nullable(),
	email: z.string().optional().nullable(),
	rawAddress: z.string().optional().nullable(),
	scoreCompanyCategoryResult: CompanyCategoryResultsSchema.nullable().optional(),
	employeesFrom: z.number().optional().nullable(),
	employeesTo: z.number().optional().nullable(),
	crmOrganizations: z.array(CrmOrganizationSchema).nullable().optional(),
	prospects: z.array(PeopleSchemaReplica).nullable().optional(),
	trackerSignalResults: z.array(TrackerSignalResultAdditionalSchema).nullable().optional(),
});

export const PeopleSchema = z.object({
	id: z.number(),
	email: z.string().nullable(),
	name: z.string().min(4, { message: REQUIRED }).trim().nullable(),
	linkedinUrl: z.string().nullable(),
	// Feeds the "Enriched at" list column (PREC-3536 Phase 5).
	lastEnrichedAt: z.string().nullable().optional(),
	firstName: z.string().optional().nullable(),
	middleName: z.string().optional().nullable(),
	lastName: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	blacklist: z.boolean().optional().nullable(),
	headline: z.string().optional().nullable(),
	emailStatus: z.string().optional().nullable(),
	// When the address was written (PREC-4262). Its source and verification are per company and
	// come on the list row, never on this shared prospect.
	emailSetAt: z.string().optional().nullable(),
	pendingEmails: z.array(ProspectPendingEmailSchema).optional().nullable(),
	seniority: z.string().optional().nullable(),
	city: z.string().optional().nullable(),
	state: z.string().optional().nullable(),
	country: z.string().optional().nullable(),
	gender: z.string().optional().nullable(),
	genderProbability: z.number().optional().nullable(),
	users: z.array(UserProspectRelationSchema).optional().nullable(),
	language: z.string().optional().nullable(),
	image: z.string().optional().nullable(),
	phone: z.string().optional().nullable(),
	phoneStatus: z.string().optional().nullable(),
	bookingLink: z.string().optional().nullable(),
	followers: z.number().optional().nullable(),
	organization: OrganizationSchema.nullable(),
	trackerSignalResults: z.array(TrackerSignalResultAdditionalSchema).optional().nullable(),
	prospectEmployments: z.array(ProspectEmploymentsSchema).optional().nullable(),
	about: z.string().nullable(),
	jobTitleToSeniority: JobTitleToSenioritySchema.nullable(),
	scorePersonaCategoryResult: PersonaCategoryResultsSchema.nullable().optional(),
	jobChangeOrPromotion: z.string().nullable(),
	startedCurrentPosition: z.string().nullable(),
	crmProspects: z.array(CrmProspectSchema).nullable().optional(),
	crmDeals: z.array(CrmDealSchema).nullable().optional(),
	crmLeads: z.array(CrmLeadSchema).nullable().optional(),
	conversations: z.array(ConversationSchema).optional().nullable(),
	snoozed: z.boolean().optional(),
	snoozedUntil: z
		.string()
		.transform((arg) => new Date(arg))
		.optional()
		.nullable(),
	sentiment: z.nativeEnum(ProspectInterest).nullable(),
	// PREC-4826: the lead unsubscribed from the caller's company (its own unsubscribe entries); the
	// List and Inbox no longer read that off `sentiment`.
	unsubscribed: z.boolean().optional(),
	trackerPersons: z.array(TrackerPersonSchema).optional().nullable(),
	deepResearchResponse: z
		.object({
			id: z.number(),
			createdAt: z.string().optional(),
			// Resolves the template name for the panel footer without an extra request.
			deepResearchPromptId: z.number().optional(),
			response: z.record(
				z.string(),
				z.object({
					order: z.number(),
					content: z.string(),
					// PREC-3680 — absent on every result produced before citations existed.
					points: z.array(DeepResearchPointSchema).optional(),
					sources: z.array(DeepResearchSourceSchema).optional(),
					subsections: z
						.array(
							z.object({
								order: z.number(),
								header: z.string(),
								content: z.string(),
								sources: z.array(DeepResearchSourceSchema).optional(),
							})
						)
						.optional(),
				})
			),
			fitAssessment: z.string().nullable().optional(),
			fitReason: z.string().nullable().optional(),
		})
		.optional()
		.nullable(),
});

export const PeopleResponseSchema = z.object({
	data: z.array(PeopleSchema),
	meta: MetaDataSchema,
});

export type People = z.infer<typeof PeopleSchema>;
export type Conversation = z.infer<typeof ConversationSchema>;
export type Organization = z.infer<typeof OrganizationSchema>;
export type PeopleResponse = z.infer<typeof PeopleResponseSchema>;
export type UserSchemaType = z.infer<typeof UserSchema>;
export type ProspectEmployments = z.infer<typeof ProspectEmploymentsSchema>;
export type CompanyCategoryResults = z.infer<typeof CompanyCategoryResultsSchema>;

// Re-exported from ./lead-scoring-breakdown.schema so existing consumers keep
// importing the persona scoring result from this barrel.
export { PersonaCategoryResultsSchema };
export type { PersonaCategoryResults };
